import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import { useDebounce } from '../../../hooks/useDebounce';
import { queryClient } from '../../../queryClient';
import {
  MutationLoaderKey,
  QueryLoaderKey,
  getLoadingAnnouncementSnapshot,
  subscribeLoadingAnnouncementStore,
  subscribeToMutationLoadingAnnouncements,
  subscribeToQueryLoadingAnnouncements,
} from './loadingAnnouncementStore';

export const LoadingAnnouncer = () => {
  const { t } = useTranslation();

  const queryPrefixLoaderMap: Record<QueryLoaderKey, string> = useMemo(
    () => ({
      myAssignments: t('loadingAssignments'),
      myCourseSections: t('loadingCourseSections'),
      myNotes: t('loadingNotes'),
      myBookmarks: t('loadingBookmarks'),
      companies: t('loadingCompanies'),
      adoptionActions: t('loadingAdoptionActions'),
      copyAssignments: t('loadingCopyAssignments'),
    }),
    [t]
  );

  const mutationKeyLoaderMap: Record<MutationLoaderKey, string> = useMemo(
    () => ({
      'myAssignments-create': t('creatingAssignment'),
      'myAssignments-update': t('updatingAssignment'),
      'myAssignments-delete': t('deletingAssignment'),
      'myCourseSections-create': t('creatingCourseSection'),
      'myCourseSections-update': t('updatingCourseSection'),
      'myCourseSections-delete': t('deletingCourseSection'),
      'myNotes-create': t('creatingNote'),
      'myNotes-update': t('updatingNote'),
      'myNotes-delete': t('deletingNote'),
      'myBookmarks-create': t('creatingBookmark'),
      'myBookmarks-update': t('updatingBookmark'),
      'myBookmarks-delete': t('deletingBookmark'),
      'companies-create': t('creatingCompany'),
      'companies-update': t('updatingCompany'),
      'companies-delete': t('deletingCompany'),
      'adoptionActions-create': t('creatingAdoptionAction'),
      'adoptionActions-update': t('updatingAdoptionAction'),
      'adoptionActions-delete': t('deletingAdoptionAction'),
      'copyAssignments-create': t('creatingCopyAssignment'),
      'copyAssignments-update': t('updatingCopyAssignment'),
      'copyAssignments-delete': t('deletingCopyAssignment'),
    }),
    [t]
  );

  const subscribe = useCallback((listener: () => void) => {
    const unsubscribeStore = subscribeLoadingAnnouncementStore(listener);

    const unsubscribeQuery = subscribeToQueryLoadingAnnouncements(queryClient.getQueryCache());
    const unsubscribeMutation = subscribeToMutationLoadingAnnouncements(
      queryClient.getMutationCache()
    );

    return () => {
      unsubscribeStore();
      unsubscribeQuery();
      unsubscribeMutation();
    };
  }, []);

  const queryAnnouncement = useSyncExternalStore(subscribe, () =>
    getLoadingAnnouncementSnapshot(queryPrefixLoaderMap, t('loadingComplete'))
  );

  const mutationAnnouncement = useSyncExternalStore(subscribe, () =>
    getLoadingAnnouncementSnapshot(mutationKeyLoaderMap, t('loadingComplete'))
  );

  const debouncedQueryAnnouncement = useDebounce(queryAnnouncement, 300);

  const debouncedMutationAnnouncement = useDebounce(mutationAnnouncement, 300);

  useEffect(() => {
    debouncedQueryAnnouncement &&
      enqueueSnackbar({ variant: 'loadingAnnouncement', message: debouncedQueryAnnouncement });
  }, [debouncedQueryAnnouncement]);

  useEffect(() => {
    debouncedMutationAnnouncement &&
      enqueueSnackbar({ variant: 'loadingAnnouncement', message: debouncedMutationAnnouncement });
  }, [debouncedMutationAnnouncement]);

  return null;
};
